import React, { useState, useEffect } from "react";
import "./distribution.less";
import { dataMap } from "@/constants/distribution";
import { rebuildFormula } from "@/config/func";
import materialsDB from "@/constants/materialsDB";
import ResultDataTable from "./DistributionResultDataTable";
import TowerEffect from "@/Components/TowerEffect";
import EffCharts from "@/Components/TowerEffect/EffCharts";
import ShowBtns from "./DistributionShowBtn";
import { Modal, Table, Select } from "antd";

// ======================
// ✅ 改动 1：新增单位映射
// ======================
export const unitMap = {
    T: ["C", "K"],
    P: ["Pa", "kPa", "MPa", "bar", "atm"],
    MC: ["mg/m³", "g/m³", "kg/m³"],
    GFR: ["kmol/h", "mol/h"],
    LFR: ["kmol/h", "mol/h"],
    MFR: ["kg/h"],
    EnthalpyV: ["J/mol", "kJ/mol"],
    EnthalpyL: ["J/mol", "kJ/mol"],
    VFR: ["m³/h"],
};

// ======================
// ✅ 改动 2：单位换算工具函数
// ======================
const convertValue = (
    value: number,
    field: string,
    fromUnit: string,
    toUnit: string
): number => {
    if (value === undefined || value === null || isNaN(value)) return 0;

    switch (field) {
        case "T":
            if (fromUnit === "K" && toUnit === "C") return value - 273.15;
            if (fromUnit === "C" && toUnit === "K") return value + 273.15;
            break;

        case "P":
            if (fromUnit === "Pa") {
                if (toUnit === "kPa") return value / 1000;
                if (toUnit === "MPa") return value / 1e6;
                if (toUnit === "bar") return value / 1e5;
                if (toUnit === "atm") return value / 101325;
            }
            if (fromUnit === "kPa" && toUnit === "Pa") return value * 1000;
            if (fromUnit === "MPa" && toUnit === "Pa") return value * 1e6;
            if (fromUnit === "bar" && toUnit === "Pa") return value * 1e5;
            if (fromUnit === "atm" && toUnit === "Pa") return value * 101325;
            break;

        case "GFR":
        case "LFR":
            if (fromUnit === "mol/h" && toUnit === "kmol/h") return value / 1000;
            if (fromUnit === "kmol/h" && toUnit === "mol/h") return value * 1000;
            break;

        case "EnthalpyV":
        case "EnthalpyL":
            if (fromUnit === "J/mol" && toUnit === "kJ/mol") return value / 1000;
            if (fromUnit === "kJ/mol" && toUnit === "J/mol") return value * 1000;
            break;

        case "MC":
            if (fromUnit === "mg/m³" && toUnit === "g/m³") return value / 1000;
            if (fromUnit === "mg/m³" && toUnit === "kg/m³") return value / 1e6;
            if (fromUnit === "g/m³" && toUnit === "kg/m³") return value / 1000;
            if (fromUnit === "g/m³" && toUnit === "mg/m³") return value * 1000;
            if (fromUnit === "kg/m³" && toUnit === "mg/m³") return value * 1e6;
            if (fromUnit === "kg/m³" && toUnit === "g/m³") return value * 1000;
            break;

        default:
            break;
    }
    return value;
};

const formatToSignificantDigits = (value: number, digits: number = 4): string => {
    if (value === 0) return "0";
    const exponent = Math.floor(Math.log10(Math.abs(value)));
    const factor = Math.pow(10, digits - 1 - exponent);
    return (Math.round(value * factor) / factor).toString();
};

const Distribution = (props: any) => {
    const { inputs, results } = props;

    const [currentMode, setCurrentMode] = useState<string>("P");
    const [currentModeData, setCurrentModeData] = useState<any[]>([]);
    const [currentMaterial, setCurrentMaterial] = useState<any>(null);
    const [currentData, setCurrentData] = useState<any>(null);
    const [showMode, setShowMode] = useState<"chart" | "table">("chart");
    const [modalStatus, setModalStatus] = useState<boolean>(false);
    const [modalTitle, setModalTitle] = useState<React.ReactNode>("");
    const [modalContent, setModalContent] = useState<React.ReactNode>("");

    // ======================
    // ✅ 改动 3：新增单位状态
    // ======================
    const [selectedUnits, setSelectedUnits] = useState({
        T: "C",
        P: "kPa",
        V: "mol/h",
        L: "mol/h",
        HV: "J/mol",
        HL: "J/mol",
        MC: "mg/m³",
    });

    // ======================
    // ✅ 改动 4：单位切换事件
    // ======================
    const handleUnitChange = (field: string, unit: string) => {
        setSelectedUnits(prev => ({
            ...prev,
            [field]: unit,
        }));
    };

    useEffect(() => {
        if (currentData !== null && currentData !== undefined) {
            buildModalSource();
        }
    }, [
        currentData,
        selectedUnits, // ✅ 改动 5：监听单位变化 → 自动刷新弹窗
    ]);

    useEffect(() => {
        if (results) {
            buildCurrentModeData();
        }
    }, [results, currentMode]);

    const buildComponentData = () => {
        const tmp = results[currentMode];
        if (!tmp || !Array.isArray(tmp) || !currentMaterial) {
            return [];
        }
        const { index } = currentMaterial;
        const componentData: any[] = [];

        // 遍历所有塔板，提取当前组分数据
        tmp.forEach((item: any) => {
            if (Array.isArray(item) && index >= 0 && index < item.length) {
                componentData.push(item[index]);
            }
        });
        return componentData;
    };

    useEffect(() => {
        if (["X", "Y", "K"].includes(currentMode) && currentMaterial) {
            const data = buildComponentData();
            setCurrentModeData(data);
        }
    }, [currentMaterial, currentMode]);

    // 构建当前模式的数据
    const buildCurrentModeData = () => {
        if (!results) return;

        // 非组分数据（T/P/V/L等）
        if (!["X", "Y", "K"].includes(currentMode)) {
            const data = results[currentMode] || [];

            // 处理HV/HL的二维数组转换
            if (["HV", "HL"].includes(currentMode) && Array.isArray(data)) {
                const flatData = data.map(item => Array.isArray(item) && item.length > 0 ? item[0] : item);
                setCurrentModeData(flatData);
            } else {
                setCurrentModeData(Array.isArray(data) ? data : []);
            }
        }
        // 组分数据（X/Y/K）- 设置默认组分
        else {
            if (inputs?.materials && inputs.materials.length > 0) {
                // 优先选择甲醇（CAS: 67-56-1）
                const methanol = inputs.materials.find((item: any) => item.cas === "67-56-1");
                setCurrentMaterial(methanol || inputs.materials[0]);
            }
        }
    };

    // 弹窗内容构建（核心）
    const buildModalSource = () => {
        if (currentData === null || !results) return;

        const title = (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 500 }}>
                    塔板 {Number(currentData) + 1} 数据
                </span>
                <Select
                    value={currentData}
                    onChange={(val) => setCurrentData(val)}
                    style={{ width: 160 }}
                >
                    {currentModeData.map((_, index) => (
                        <Select.Option key={index} value={index}>
                            塔板 {index + 1}
                        </Select.Option>
                    ))}
                </Select>
            </div>
        );

        // 弹窗内容
        const content = (
            <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                <div style={{ marginBottom: 20 }}>
                    <h4 style={{ marginBottom: 10 }}>基础参数</h4>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 10,
                        }}
                    >
                        {Object.keys(dataMap)
                            .filter((key) => !["K", "X", "Y"].includes(key))
                            .map((key) => {
                                // ======================
                                // ✅ 改动 6：获取原始值 & 单位
                                // ======================
                                const rawValue = results[key]?.[currentData];
                                const rawUnit = dataMap[key].unit;
                                let field = key;

                                if (key === "V") field = "GFR";
                                if (key === "L") field = "LFR";
                                if (key === "HV") field = "EnthalpyV";
                                if (key === "HL") field = "EnthalpyL";

                                const targetUnit = selectedUnits[field] || rawUnit;
                                const convertedValue = convertValue(
                                    rawValue,
                                    field,
                                    rawUnit,
                                    targetUnit
                                );

                                // ======================
                                // ✅ 改动 7：渲染带下拉选择器的参数项
                                // ======================
                                return (
                                    <div
                                        key={key}
                                        style={{
                                            padding: 8,
                                            border: "1px solid #f0f0f0",
                                            borderRadius: 4,
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "center",  // 新增：垂直居中对齐
                                            justifyContent: "space-between",  // 新增：两端对齐
                                        }}
                                    >
                                        {/* 左侧：标题 + 数据 */}
                                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                            <span style={{ color: "#666" }}>{dataMap[key]?.title}:&nbsp;</span>
                                            <span style={{ color: "#0148e0" }}>
                                                {/* {formatToSignificantDigits(convertedValue)} */}
                                                {convertedValue}
                                            </span>
                                        </div>

                                        {/* 右侧：单位选择器 */}
                                        {unitMap[field as keyof typeof unitMap] && (
                                            <Select
                                                size="small"
                                                value={targetUnit}
                                                onChange={(unit) => handleUnitChange(field, unit)}
                                                style={{ width: 90 }}
                                            >
                                                {unitMap[field as keyof typeof unitMap].map((u) => (
                                                    <Select.Option key={u} value={u}>
                                                        {u}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                </div>

                {/* 组分表格 */}
                <div>
                    <h4 style={{ marginBottom: 10 }}>组分数据</h4>
                    <Table
                        size="small"
                        bordered
                        pagination={false}
                        columns={[
                            { title: "组分", dataIndex: "name", width: 180 },
                            { title: "液相摩尔分率", dataIndex: "x" },
                            { title: "气相摩尔分率", dataIndex: "y" },
                            { title: "相平衡常数", dataIndex: "k" },
                            { title: "摩尔流量(mol/h)", dataIndex: "molFlow" },
                            { title: "质量流量(g/h)", dataIndex: "massFlow" },
                            { title: "质量浓度(mg/m³)", dataIndex: "massCon" },
                        ]}
                        dataSource={inputs.materials.map((material, index) => {
                            const xValue = results["X"]?.[currentData]?.[index] ?? "-";
                            const yValue = results["Y"]?.[currentData]?.[index] ?? "-";
                            const kValue = results["K"]?.[currentData]?.[index] ?? "-";

                            const liq = results["L"]?.[currentData] ?? 0;
                            const vap = results["V"]?.[currentData] ?? 0;
                            const t = results["T"]?.[currentData] ?? 298.15;
                            const p = results["P"]?.[currentData] ?? 101325;

                            let molFlow = "-";
                            if (xValue !== "-") {
                                molFlow = formatToSignificantDigits(Number(xValue) * liq);
                            } else if (yValue !== "-") {
                                molFlow = formatToSignificantDigits(Number(yValue) * vap);
                            }

                            let massFlow = "-";
                            if (xValue !== "-") {
                                massFlow = formatToSignificantDigits(Number(xValue) * liq * material.MW / 1000);
                            } else if (yValue !== "-") {
                                massFlow = formatToSignificantDigits(Number(yValue) * vap * material.MW / 1000);
                            }

                            let massCon = "-";
                            if (xValue !== "-") {
                                massCon = formatToSignificantDigits(
                                    (Number(xValue) * p * material.MW) / (8.314 * t)
                                );
                            } else if (yValue !== "-") {
                                massCon = formatToSignificantDigits(
                                    (Number(yValue) * p * material.MW) / (8.314 * t)
                                );
                            }

                            return {
                                key: index,
                                name: `${rebuildFormula(material.Molecular_formula)}（${material.Name_ZH}）`,
                                x: xValue === "-" ? "-" : formatToSignificantDigits(Number(xValue)),
                                y: yValue === "-" ? "-" : formatToSignificantDigits(Number(yValue)),
                                k: kValue,
                                molFlow,
                                massFlow,
                                massCon,
                            };
                        })}
                    />
                </div>
            </div>
        );

        setModalTitle(title);
        setModalContent(content);
    };

    const getMaxValue = () => {
        if (!Array.isArray(currentModeData) || currentModeData.length === 0) return 1;
        const valid = currentModeData.flat().filter(v => !isNaN(Number(v)) && isFinite(Number(v)));
        if (valid.length === 0) return 1;
        const max = Math.max(...valid);
        const min = Math.min(...valid);
        const range = Math.abs(max - min);
        return max + range * 0.1;
    };

    const getMinValue = () => {
        if (!Array.isArray(currentModeData) || currentModeData.length === 0) return 0;
        const valid = currentModeData.flat().filter(v => !isNaN(Number(v)) && isFinite(Number(v)));
        if (valid.length === 0) return 0;
        const max = Math.max(...valid);
        const min = Math.min(...valid);
        const range = Math.abs(max - min);
        return min >= 0 ? Math.max(0, min - range * 0.1) : min - range * 0.1;
    };

    const getChartTitle = () => {
        if (["K", "X", "Y"].includes(currentMode) && currentMaterial) {
            return `${rebuildFormula(currentMaterial?.Molecular_formula)}${dataMap[currentMode]?.title || ""}`;
        } else {
            return `${dataMap[currentMode]?.title || currentMode}分布图`;
        }
    };

    const showMF = (item: any) => {
        const mat = materialsDB.find(i => i.cas === item.cas);
        return mat ? rebuildFormula(mat.Molecular_formula) : rebuildFormula(item.Molecular_formula || item.name);
    };

    const isLoading = !currentModeData || currentModeData.length === 0;

    return (
        <div className="single-result-container">
            <div className="show-btns-wrapper">
                <ShowBtns
                    currentMode={currentMode}
                    setCurrentMode={setCurrentMode}
                    showMode={showMode}
                    setShowMode={setShowMode}
                    setCurrentData={setCurrentData}
                />
            </div>

            {isLoading ? (
                <div className="loading-area">
                    <p>正在加载数据...</p>
                    <p className="loading-mode">当前模式: {currentMode}</p>
                </div>
            ) : (
                <div className="main-content-wrapper">
                    {/* 左侧塔板效果图 */}
                    <div className="tower-effect-wrapper">
                        <TowerEffect
                            currentMode={currentMode}
                            setModalStatus={setModalStatus}
                            currentData={currentData}
                            setCurrentData={setCurrentData}
                            currentModeData={currentModeData}
                            wrapWidth={280}
                            trayHeight={310}
                            imageStyle={{ height: "420px", width: "80px" }}
                            trayContainerStyle={{ top: "40px", left: "27px", width: "47px" }}
                        />
                    </div>

                    <div className="data-show-area">
                        <div className="line-chart-container">
                            {["X", "Y", "K"].includes(currentMode) && inputs?.materials && (
                                <div className="materials-select-container">
                                    {inputs.materials.map((i: any, index) => {
                                        const isActive = currentMaterial?.cas === i.cas;
                                        return (
                                            <div key={index} className="material-item">
                                                <button
                                                    className={`material-btn ${isActive ? "active" : ""}`}
                                                    onClick={() => setCurrentMaterial({ ...i, index })}
                                                >
                                                    <span className={`material-btn-text ${isActive ? "active" : ""}`}>
                                                        {showMF(i)}
                                                    </span>
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {showMode === "chart" ? (
                                <EffCharts
                                    chartData={currentModeData}
                                    title={`塔板${getChartTitle()}`}
                                    currentMode={currentMode}
                                    height={470}
                                    min={getMinValue()}
                                    max={getMaxValue()}
                                />
                            ) : (
                                <ResultDataTable
                                    results={results}
                                    inputMaterials={inputs.materials}
                                    currentMode={currentMode}
                                    currentModeData={currentModeData}
                                    currentMaterial={currentMaterial}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            <Modal
                title={modalTitle}
                open={modalStatus}
                onCancel={() => {
                    setModalStatus(false);
                    setCurrentData(null);
                }}
                width={1300}
                destroyOnClose
            >
                {modalContent}
            </Modal>
        </div>
    );
};

export default Distribution;